import { Platform, Pressable, Text, View } from 'react-native';
import { twMerge } from 'tailwind-merge';

interface SegmentedControlOption<T extends string> {
  label: string;
  value: T;
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'sm',
}: SegmentedControlProps<T>) {
  const containerClass = size === 'sm' ? 'p-1' : 'p-1.5';
  const itemClass = size === 'sm' ? 'px-3 py-2' : 'px-4 py-2.5';
  const textClass = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <View className={twMerge('flex-row rounded-full bg-gray-100 dark:bg-gray-700', containerClass)}>
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive }}
            className={twMerge(
              'rounded-full',
              itemClass,
              isActive ? 'bg-white dark:bg-gray-600' : '',
            )}
            style={isActive && Platform.OS !== 'web' ? { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 } : undefined}
            onPress={() => onChange(option.value)}
          >
            <Text
              className={twMerge(
                'font-medium',
                textClass,
                isActive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400',
              )}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
